import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { API_BASE } from '../../config/api';

export const AdminSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [activeTab, setActiveTab] = useState('account');
  
  // Profile/Account Settings
  const [profileData, setProfileData] = useState({
    email: '',
    full_name: '',
    backup_email: ''
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Email Settings
  const [emailSettings, setEmailSettings] = useState({
    resend_api_key: '',
    sender_email: '',
    sender_name: '',
    email_enabled: true
  });

  // WhatsApp Settings
  const [whatsappSettings, setWhatsappSettings] = useState({
    whatsapp_number: '',
    default_message: 'Hi, I would like to inquire about studying abroad',
    whatsapp_enabled: true
  });

  // Analytics Settings
  const [analyticsSettings, setAnalyticsSettings] = useState({
    ga4: {
      enabled: false,
      measurement_id: ''
    },
    posthog: {
      enabled: false,
      api_key: '',
      host: 'https://app.posthog.com'
    },
    gtm: {
      enabled: false,
      container_id: ''
    }
  });

  const [testEmail, setTestEmail] = useState('');
  const [message, setMessage] = useState(null);

  // Integrations Settings
  const [integrations, setIntegrations] = useState([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [showIntegrationForm, setShowIntegrationForm] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [integrationFormData, setIntegrationFormData] = useState({
    id: '',
    type: 'script',
    placement: 'head',
    enabled: false,
    content: '',
    uniqueKey: '',
    requiresConsent: false,
    env: ['production']
  });
  const [integrationFormErrors, setIntegrationFormErrors] = useState({});

  // Simple UUID generator
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin/login');
        return;
      }

      // Fetch admin profile
      const profileResponse = await fetch(`${backendUrl}/api/admin/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (profileResponse.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (profileResponse.ok) {
        const data = await profileResponse.json();
        setProfileData({
          email: data.admin.email || '',
          full_name: data.admin.full_name || '',
          backup_email: data.admin.backup_email || ''
        });
      }

      // Load default email/whatsapp settings
      setEmailSettings({
        resend_api_key: '',
        sender_email: '',
        sender_name: '',
        email_enabled: true
      });

      setWhatsappSettings({
        whatsapp_number: '60123456789',
        default_message: 'Hi, I would like to inquire about studying abroad',
        whatsapp_enabled: true
      });

      // Fetch analytics settings
      const analyticsResponse = await fetch(`${backendUrl}/api/admin/analytics-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (analyticsResponse.ok) {
        const analyticsData = await analyticsResponse.json();
        if (analyticsData.settings) {
          setAnalyticsSettings({
            ga4: analyticsData.settings.ga4 || { enabled: false, measurement_id: '' },
            posthog: analyticsData.settings.posthog || { enabled: false, api_key: '', host: 'https://app.posthog.com' },
            gtm: analyticsData.settings.gtm || { enabled: false, container_id: '' }
          });
        }
      }

      // Fetch integrations settings
      const integrationsResponse = await fetch(`${backendUrl}/api/admin/integrations-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (integrationsResponse.ok) {
        const integrationsData = await integrationsResponse.json();
        if (integrationsData.settings && integrationsData.settings.items) {
          setIntegrations(integrationsData.settings.items || []);
        }
      }

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          full_name: profileData.full_name,
          backup_email: profileData.backup_email
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Profile updated successfully' });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to update profile' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordData.new_password.length < 8) {
      setMessage({ type: 'error', text: 'New password must be at least 8 characters' });
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Password changed successfully' });
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to change password' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to change password' });
    } finally {
      setSaving(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!profileData.backup_email) {
      setMessage({ type: 'error', text: 'Please set a backup email first to use password reset' });
      return;
    }

    setSendingReset(true);
    setMessage(null);

    try {
      const response = await fetch(`${backendUrl}/api/admin/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: profileData.email })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Password reset link sent to your backup email' });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to send reset link' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to send reset link' });
    } finally {
      setSendingReset(false);
    }
  };

  const handleSaveEmailSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setMessage({ type: 'success', text: 'Email settings saved successfully' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save email settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWhatsAppSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setMessage({ type: 'success', text: 'WhatsApp settings saved successfully' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save WhatsApp settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAnalyticsSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/analytics-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(analyticsSettings)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Analytics settings saved successfully' });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to save analytics settings' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save analytics settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      setMessage({ type: 'error', text: 'Please enter a test email address' });
      return;
    }

    setSendingTest(true);
    setMessage(null);

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setMessage({ type: 'success', text: `Test email sent to ${testEmail}` });
      setTestEmail('');
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to send test email' });
    } finally {
      setSendingTest(false);
    }
  };

  // Integrations Handlers
  const fetchIntegrations = async () => {
    try {
      setIntegrationsLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/integrations-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.settings && data.settings.items) {
          setIntegrations(data.settings.items || []);
        }
      }
    } catch (err) {
      console.error('Failed to fetch integrations:', err);
    } finally {
      setIntegrationsLoading(false);
    }
  };

  const validateIntegrationForm = () => {
    const errors = {};
    
    if (!integrationFormData.id.trim()) {
      errors.id = 'ID is required';
    }
    
    if (!integrationFormData.content.trim()) {
      errors.content = 'Content is required';
    }
    
    // Validate uniqueKey for meta/link types
    if ((integrationFormData.type === 'meta' || integrationFormData.type === 'link') && !integrationFormData.uniqueKey.trim()) {
      errors.uniqueKey = 'Unique Key is required for meta and link types';
    }
    
    // Validate placement for meta/link/jsonld (must be head)
    if ((integrationFormData.type === 'meta' || integrationFormData.type === 'link' || integrationFormData.type === 'jsonld') && integrationFormData.placement !== 'head') {
      errors.placement = `${integrationFormData.type} type must be placed in head`;
    }
    
    setIntegrationFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveIntegration = async (e) => {
    e.preventDefault();
    
    if (!validateIntegrationForm()) {
      setMessage({ type: 'error', text: 'Please fix validation errors' });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('admin_token');
      const updatedItems = [...integrations];
      
      if (editingIntegration) {
        // Update existing
        const index = updatedItems.findIndex(item => item.id === editingIntegration.id);
        if (index !== -1) {
          updatedItems[index] = {
            ...integrationFormData,
            updated_at: new Date().toISOString()
          };
        }
      } else {
        // Add new
        updatedItems.push({
          ...integrationFormData,
          id: integrationFormData.id || generateUUID(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      }

      const response = await fetch(`${backendUrl}/api/admin/integrations-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items: updatedItems })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: editingIntegration ? 'Integration updated successfully' : 'Integration added successfully' });
        setShowIntegrationForm(false);
        setEditingIntegration(null);
        setIntegrationFormData({
          id: '',
          type: 'script',
          placement: 'head',
          enabled: false,
          content: '',
          uniqueKey: '',
          requiresConsent: false,
          env: ['production']
        });
        setIntegrationFormErrors({});
        fetchIntegrations();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to save integration' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save integration' });
    } finally {
      setSaving(false);
    }
  };

  const handleEditIntegration = (integration) => {
    setEditingIntegration(integration);
    setIntegrationFormData({
      id: integration.id,
      type: integration.type,
      placement: integration.placement,
      enabled: integration.enabled,
      content: integration.content,
      uniqueKey: integration.uniqueKey || '',
      requiresConsent: integration.requiresConsent || false,
      env: integration.env || ['production']
    });
    setShowIntegrationForm(true);
    setIntegrationFormErrors({});
  };

  const handleDeleteIntegration = async (integrationId) => {
    if (!window.confirm('Are you sure you want to delete this integration? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const updatedItems = integrations.filter(item => item.id !== integrationId);

      const response = await fetch(`${backendUrl}/api/admin/integrations-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items: updatedItems })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Integration deleted successfully' });
        fetchIntegrations();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to delete integration' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete integration' });
    }
  };

  const handleToggleIntegration = async (integration) => {
    try {
      const token = localStorage.getItem('admin_token');
      const updatedItems = integrations.map(item => 
        item.id === integration.id 
          ? { ...item, enabled: !item.enabled, updated_at: new Date().toISOString() }
          : item
      );

      const response = await fetch(`${backendUrl}/api/admin/integrations-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items: updatedItems })
      });

      if (response.ok) {
        fetchIntegrations();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to toggle integration' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to toggle integration' });
    }
  };

  const handleCancelIntegrationForm = () => {
    setShowIntegrationForm(false);
    setEditingIntegration(null);
    setIntegrationFormData({
      id: '',
      type: 'script',
      placement: 'head',
      enabled: false,
      content: '',
      uniqueKey: '',
      requiresConsent: false,
      env: ['production']
    });
    setIntegrationFormErrors({});
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[#6B7280]">Loading settings...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const tabs = [
    { id: 'account', label: 'Account', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    )},
    { id: 'password', label: 'Password', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    )},
    { id: 'email', label: 'Email', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    )},
    { id: 'whatsapp', label: 'WhatsApp', icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    )},
    { id: 'analytics', label: 'Analytics', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    )},
    { id: 'integrations', label: 'Integrations', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    )}
  ];

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#111827]" data-testid="settings-title">Settings</h1>
          <p className="text-[#6B7280] text-sm mt-1">Manage your account and integrations</p>
        </div>

        {/* Message */}
        {message && (
          <div className={`p-4 rounded-xl border ${
            message.type === 'success' 
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700' 
              : 'bg-red-50 border-red-200 text-red-700'
          }`} data-testid="message-alert">
            <div className="flex items-center gap-3">
              {message.type === 'success' ? (
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              <p className="font-medium text-sm">{message.text}</p>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-1.5 inline-flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setMessage(null);
              }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#1E40AF] text-white'
                  : 'text-[#6B7280] hover:bg-[#F3F4F6]'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Account Settings Tab */}
        {activeTab === 'account' && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#EFF6FF] rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-[#1E40AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#111827]">Profile Information</h2>
                <p className="text-sm text-[#6B7280]">Update your account details</p>
              </div>
            </div>

            <form onSubmit={handleUpdateProfile} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">Email Address</label>
                <input
                  type="email"
                  value={profileData.email}
                  disabled
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg bg-[#F9FAFB] text-[#6B7280] cursor-not-allowed"
                  data-testid="profile-email"
                />
                <p className="text-xs text-[#9CA3AF] mt-1.5">Email address cannot be changed</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">Full Name</label>
                <input
                  type="text"
                  value={profileData.full_name}
                  onChange={(e) => setProfileData({...profileData, full_name: e.target.value})}
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                  data-testid="profile-full-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">Backup Email</label>
                <input
                  type="email"
                  value={profileData.backup_email}
                  onChange={(e) => setProfileData({...profileData, backup_email: e.target.value})}
                  placeholder="backup@example.com"
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                  data-testid="profile-backup-email"
                />
                <p className="text-xs text-[#9CA3AF] mt-1.5">Used for password recovery. Make sure this is a different email you have access to.</p>
              </div>

              <button
                type="submit"
                disabled={saving}
                className="px-6 py-3 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                data-testid="save-profile"
              >
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </form>
          </div>
        )}

        {/* Password Tab */}
        {activeTab === 'password' && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#111827]">Change Password</h2>
                <p className="text-sm text-[#6B7280]">Update your password to keep your account secure</p>
              </div>
            </div>

            <form onSubmit={handleChangePassword} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">Current Password</label>
                <input
                  type="password"
                  required
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                  data-testid="current-password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">New Password</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                  data-testid="new-password"
                />
                <p className="text-xs text-[#9CA3AF] mt-1.5">Minimum 8 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">Confirm New Password</label>
                <input
                  type="password"
                  required
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                  data-testid="confirm-password"
                />
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-3 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                  data-testid="change-password-btn"
                >
                  {saving ? 'Changing...' : 'Change Password'}
                </button>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={sendingReset || !profileData.backup_email}
                  className="px-6 py-3 text-[#1E40AF] hover:bg-[#EFF6FF] font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="send-reset-link"
                  title={!profileData.backup_email ? 'Set a backup email first' : ''}
                >
                  {sendingReset ? 'Sending...' : 'Send Reset Link'}
                </button>
              </div>

              {!profileData.backup_email && (
                <p className="text-sm text-amber-600 bg-amber-50 px-4 py-3 rounded-lg border border-amber-200">
                  <strong>Note:</strong> Set a backup email in the Account tab to enable password reset via email.
                </p>
              )}
            </form>
          </div>
        )}

        {/* Email Settings Tab */}
        {activeTab === 'email' && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#EFF6FF] rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-[#1E40AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#111827]">Email Integration (Resend)</h2>
                <p className="text-sm text-[#6B7280]">Configure email notifications for form submissions</p>
              </div>
            </div>

            <form onSubmit={handleSaveEmailSettings} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">Resend API Key</label>
                <input
                  type="password"
                  value={emailSettings.resend_api_key}
                  onChange={(e) => setEmailSettings({...emailSettings, resend_api_key: e.target.value})}
                  placeholder="re_xxxxxxxxxxxx"
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                  data-testid="resend-api-key"
                />
                <p className="text-xs text-[#9CA3AF] mt-1.5">Get your API key from <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-[#1E40AF] hover:underline">Resend Dashboard</a></p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-2">Sender Email</label>
                  <input
                    type="email"
                    value={emailSettings.sender_email}
                    onChange={(e) => setEmailSettings({...emailSettings, sender_email: e.target.value})}
                    placeholder="noreply@yourdomain.com"
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                    data-testid="sender-email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-2">Sender Name</label>
                  <input
                    type="text"
                    value={emailSettings.sender_name}
                    onChange={(e) => setEmailSettings({...emailSettings, sender_name: e.target.value})}
                    placeholder="Unisave Team"
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                    data-testid="sender-name"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="email-enabled"
                  checked={emailSettings.email_enabled}
                  onChange={(e) => setEmailSettings({...emailSettings, email_enabled: e.target.checked})}
                  className="w-5 h-5 text-[#1E40AF] border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#1E40AF]"
                  data-testid="email-enabled"
                />
                <label htmlFor="email-enabled" className="text-sm font-medium text-[#374151] cursor-pointer">
                  Enable Email Notifications
                </label>
              </div>

              <div className="pt-5 border-t border-[#E5E7EB]">
                <p className="text-sm font-medium text-[#374151] mb-3">Send Test Email</p>
                <div className="flex gap-3">
                  <input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="test@example.com"
                    className="flex-1 px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                    data-testid="test-email"
                  />
                  <button
                    type="button"
                    onClick={handleSendTestEmail}
                    disabled={sendingTest}
                    className="px-6 py-3 bg-[#F3F4F6] border border-[#E5E7EB] text-[#374151] hover:bg-[#E5E7EB] rounded-lg font-medium transition-colors disabled:opacity-50"
                    data-testid="send-test-email"
                  >
                    {sendingTest ? 'Sending...' : 'Send Test'}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={saving}
                className="px-6 py-3 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                data-testid="save-email-settings"
              >
                {saving ? 'Saving...' : 'Save Email Settings'}
              </button>
            </form>
          </div>
        )}

        {/* WhatsApp Settings Tab */}
        {activeTab === 'whatsapp' && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#111827]">WhatsApp Configuration</h2>
                <p className="text-sm text-[#6B7280]">Configure the floating WhatsApp button</p>
              </div>
            </div>

            <form onSubmit={handleSaveWhatsAppSettings} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">WhatsApp Number (with country code)</label>
                <input
                  type="tel"
                  value={whatsappSettings.whatsapp_number}
                  onChange={(e) => setWhatsappSettings({...whatsappSettings, whatsapp_number: e.target.value})}
                  placeholder="60123456789"
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                  data-testid="whatsapp-number"
                />
                <p className="text-xs text-[#9CA3AF] mt-1.5">Example: 60123456789 (Malaysia)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">Default Message Template</label>
                <textarea
                  value={whatsappSettings.default_message}
                  onChange={(e) => setWhatsappSettings({...whatsappSettings, default_message: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                  data-testid="default-message"
                />
                <p className="text-xs text-[#9CA3AF] mt-1.5">This message will be pre-filled when users click the WhatsApp button</p>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="whatsapp-enabled"
                  checked={whatsappSettings.whatsapp_enabled}
                  onChange={(e) => setWhatsappSettings({...whatsappSettings, whatsapp_enabled: e.target.checked})}
                  className="w-5 h-5 text-[#1E40AF] border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#1E40AF]"
                  data-testid="whatsapp-enabled"
                />
                <label htmlFor="whatsapp-enabled" className="text-sm font-medium text-[#374151] cursor-pointer">
                  Enable WhatsApp Button on Website
                </label>
              </div>

              <div className="p-4 bg-[#F9FAFB] rounded-lg border border-[#E5E7EB]">
                <p className="text-sm text-[#6B7280]">
                  <strong>Note:</strong> When enabled, the floating WhatsApp button will appear on all public pages using the number and message configured above.
                </p>
              </div>

              <button
                type="submit"
                disabled={saving}
                className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                data-testid="save-whatsapp-settings"
              >
                {saving ? 'Saving...' : 'Save WhatsApp Settings'}
              </button>
            </form>
          </div>
        )}

        {/* Analytics Settings Tab */}
        {activeTab === 'analytics' && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#111827]">Analytics Configuration</h2>
                <p className="text-sm text-[#6B7280]">Configure Google Analytics 4 and PostHog</p>
              </div>
            </div>

            <form onSubmit={handleSaveAnalyticsSettings} className="space-y-6">
              {/* Google Analytics 4 Section */}
              <div className="border border-[#E5E7EB] rounded-lg p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-base font-semibold text-[#111827]">Google Analytics 4</h3>
                    <p className="text-xs text-[#6B7280] mt-1">Track website traffic and user behavior</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={analyticsSettings.ga4.enabled}
                      onChange={(e) => setAnalyticsSettings({
                        ...analyticsSettings,
                        ga4: { ...analyticsSettings.ga4, enabled: e.target.checked }
                      })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#1E40AF]"></div>
                  </label>
                </div>

                {analyticsSettings.ga4.enabled && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-[#374151] mb-2">
                      GA4 Measurement ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required={analyticsSettings.ga4.enabled}
                      value={analyticsSettings.ga4.measurement_id}
                      onChange={(e) => setAnalyticsSettings({
                        ...analyticsSettings,
                        ga4: { ...analyticsSettings.ga4, measurement_id: e.target.value }
                      })}
                      placeholder="G-XXXXXXXXXX"
                      className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                    />
                    <p className="text-xs text-[#9CA3AF] mt-1.5">
                      Find your Measurement ID in GA4 Admin → Data Streams → Web Stream
                    </p>
                  </div>
                )}
              </div>

              {/* PostHog Section */}
              <div className="border border-[#E5E7EB] rounded-lg p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-base font-semibold text-[#111827]">PostHog</h3>
                    <p className="text-xs text-[#6B7280] mt-1">Product analytics and feature flags</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={analyticsSettings.posthog.enabled}
                      onChange={(e) => setAnalyticsSettings({
                        ...analyticsSettings,
                        posthog: { ...analyticsSettings.posthog, enabled: e.target.checked }
                      })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#1E40AF]"></div>
                  </label>
                </div>

                {analyticsSettings.posthog.enabled && (
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">
                        PostHog API Key <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="password"
                        required={analyticsSettings.posthog.enabled}
                        value={analyticsSettings.posthog.api_key}
                        onChange={(e) => setAnalyticsSettings({
                          ...analyticsSettings,
                          posthog: { ...analyticsSettings.posthog, api_key: e.target.value }
                        })}
                        placeholder="phc_xxxxxxxxxxxx"
                        className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                      />
                      <p className="text-xs text-[#9CA3AF] mt-1.5">
                        Find your API key in PostHog Settings → Project → API Key
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">PostHog Host</label>
                      <input
                        type="url"
                        value={analyticsSettings.posthog.host}
                        onChange={(e) => setAnalyticsSettings({
                          ...analyticsSettings,
                          posthog: { ...analyticsSettings.posthog, host: e.target.value }
                        })}
                        placeholder="https://app.posthog.com"
                        className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                      />
                      <p className="text-xs text-[#9CA3AF] mt-1.5">
                        Default: https://app.posthog.com (or your self-hosted instance)
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Google Tag Manager Section */}
              <div className="border border-[#E5E7EB] rounded-lg p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-base font-semibold text-[#111827]">Google Tag Manager</h3>
                    <p className="text-xs text-[#6B7280] mt-1">Manage tags and tracking via GTM</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={analyticsSettings.gtm.enabled}
                      onChange={(e) => setAnalyticsSettings({
                        ...analyticsSettings,
                        gtm: { ...analyticsSettings.gtm, enabled: e.target.checked }
                      })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#1E40AF]"></div>
                  </label>
                </div>

                {analyticsSettings.gtm.enabled && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-[#374151] mb-2">
                      GTM Container ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required={analyticsSettings.gtm.enabled}
                      value={analyticsSettings.gtm.container_id}
                      onChange={(e) => setAnalyticsSettings({
                        ...analyticsSettings,
                        gtm: { ...analyticsSettings.gtm, container_id: e.target.value }
                      })}
                      placeholder="GTM-XXXXXXX"
                      className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                    />
                    <p className="text-xs text-[#9CA3AF] mt-1.5">
                      Find your Container ID in GTM Admin → Container Settings. Format: GTM-XXXXXXX
                    </p>
                  </div>
                )}
              </div>

              <div className="p-4 bg-[#F9FAFB] rounded-lg border border-[#E5E7EB]">
                <p className="text-sm text-[#6B7280]">
                  <strong>Note:</strong> Analytics scripts will only load if enabled and a valid key/ID is provided. 
                  Changes take effect immediately without redeployment. Analytics are disabled in development mode.
                </p>
              </div>

              <button
                type="submit"
                disabled={saving}
                className="px-6 py-3 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Analytics Settings'}
              </button>
            </form>
          </div>
        )}

        {/* Integrations Settings Tab */}
        {activeTab === 'integrations' && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[#111827]">Head & Integration Manager</h2>
                  <p className="text-sm text-[#6B7280]">Manage scripts, meta tags, link tags, and JSON-LD schemas</p>
                </div>
              </div>
              {!showIntegrationForm && (
                <button
                  onClick={() => {
                    setShowIntegrationForm(true);
                    setEditingIntegration(null);
                    setIntegrationFormData({
                      id: '',
                      type: 'script',
                      placement: 'head',
                      enabled: false,
                      content: '',
                      uniqueKey: '',
                      requiresConsent: false,
                      env: ['production']
                    });
                  }}
                  className="px-4 py-2 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-medium rounded-lg transition-colors"
                >
                  + Add Integration
                </button>
              )}
            </div>

            {showIntegrationForm ? (
              <form onSubmit={handleSaveIntegration} className="space-y-6">
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Warning:</strong> This affects the entire website. Changes take effect immediately.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-2">
                      ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={integrationFormData.id}
                      onChange={(e) => setIntegrationFormData({ ...integrationFormData, id: e.target.value })}
                      placeholder="unique-integration-id"
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] ${
                        integrationFormErrors.id ? 'border-red-500' : 'border-[#E5E7EB]'
                      }`}
                      required
                    />
                    {integrationFormErrors.id && (
                      <p className="text-xs text-red-500 mt-1">{integrationFormErrors.id}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-2">
                      Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={integrationFormData.type}
                      onChange={(e) => {
                        const newType = e.target.value;
                        let newPlacement = integrationFormData.placement;
                        // Auto-set placement for meta/link/jsonld
                        if (newType === 'meta' || newType === 'link' || newType === 'jsonld') {
                          newPlacement = 'head';
                        }
                        setIntegrationFormData({ 
                          ...integrationFormData, 
                          type: newType,
                          placement: newPlacement
                        });
                      }}
                      className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF]"
                      required
                    >
                      <option value="script">Script</option>
                      <option value="meta">Meta Tag</option>
                      <option value="link">Link Tag</option>
                      <option value="jsonld">JSON-LD</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-2">
                      Placement <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={integrationFormData.placement}
                      onChange={(e) => setIntegrationFormData({ ...integrationFormData, placement: e.target.value })}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] ${
                        integrationFormErrors.placement ? 'border-red-500' : 'border-[#E5E7EB]'
                      }`}
                      required
                      disabled={integrationFormData.type === 'meta' || integrationFormData.type === 'link' || integrationFormData.type === 'jsonld'}
                    >
                      <option value="head">Head</option>
                      <option value="body_start">Body Start</option>
                      <option value="body_end">Body End</option>
                    </select>
                    {integrationFormErrors.placement && (
                      <p className="text-xs text-red-500 mt-1">{integrationFormErrors.placement}</p>
                    )}
                    {(integrationFormData.type === 'meta' || integrationFormData.type === 'link' || integrationFormData.type === 'jsonld') && (
                      <p className="text-xs text-[#6B7280] mt-1">Must be placed in head</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-2">
                      Unique Key {integrationFormData.type === 'meta' || integrationFormData.type === 'link' ? <span className="text-red-500">*</span> : ''}
                    </label>
                    <input
                      type="text"
                      value={integrationFormData.uniqueKey}
                      onChange={(e) => setIntegrationFormData({ ...integrationFormData, uniqueKey: e.target.value })}
                      placeholder="unique-key-for-deduplication"
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] ${
                        integrationFormErrors.uniqueKey ? 'border-red-500' : 'border-[#E5E7EB]'
                      }`}
                      required={integrationFormData.type === 'meta' || integrationFormData.type === 'link'}
                    />
                    {integrationFormErrors.uniqueKey && (
                      <p className="text-xs text-red-500 mt-1">{integrationFormErrors.uniqueKey}</p>
                    )}
                    <p className="text-xs text-[#6B7280] mt-1">
                      Required for meta/link types to prevent duplicates
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-2">
                    Content <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={integrationFormData.content}
                    onChange={(e) => setIntegrationFormData({ ...integrationFormData, content: e.target.value })}
                    placeholder={integrationFormData.type === 'script' ? '<script src="..."></script>' : integrationFormData.type === 'meta' ? '<meta name="..." content="...">' : integrationFormData.type === 'link' ? '<link rel="..." href="...">' : '{"@context": "https://schema.org", ...}'}
                    rows={6}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] font-mono text-sm ${
                      integrationFormErrors.content ? 'border-red-500' : 'border-[#E5E7EB]'
                    }`}
                    required
                  />
                  {integrationFormErrors.content && (
                    <p className="text-xs text-red-500 mt-1">{integrationFormErrors.content}</p>
                  )}
                </div>

                <div className="flex items-center gap-6">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={integrationFormData.enabled}
                      onChange={(e) => setIntegrationFormData({ ...integrationFormData, enabled: e.target.checked })}
                      className="w-4 h-4 text-[#1E40AF] border-gray-300 rounded focus:ring-[#1E40AF]"
                    />
                    <span className="ml-2 text-sm text-[#374151]">Enabled</span>
                  </label>

                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={integrationFormData.requiresConsent}
                      onChange={(e) => setIntegrationFormData({ ...integrationFormData, requiresConsent: e.target.checked })}
                      className="w-4 h-4 text-[#1E40AF] border-gray-300 rounded focus:ring-[#1E40AF]"
                    />
                    <span className="ml-2 text-sm text-[#374151]">Requires Consent</span>
                  </label>

                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={integrationFormData.env.includes('production')}
                      onChange={(e) => {
                        const env = e.target.checked
                          ? [...integrationFormData.env.filter(e => e !== 'production'), 'production']
                          : integrationFormData.env.filter(e => e !== 'production');
                        setIntegrationFormData({ ...integrationFormData, env });
                      }}
                      className="w-4 h-4 text-[#1E40AF] border-gray-300 rounded focus:ring-[#1E40AF]"
                    />
                    <span className="ml-2 text-sm text-[#374151]">Production Environment</span>
                  </label>
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-6 py-3 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : editingIntegration ? 'Update Integration' : 'Add Integration'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelIntegrationForm}
                    className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <>
                {integrationsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
                      <p className="text-[#6B7280]">Loading integrations...</p>
                    </div>
                  </div>
                ) : integrations.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-[#6B7280] mb-4">No integrations configured yet.</p>
                    <button
                      onClick={() => {
                        setShowIntegrationForm(true);
                        setEditingIntegration(null);
                        setIntegrationFormData({
                          id: '',
                          type: 'script',
                          placement: 'head',
                          enabled: false,
                          content: '',
                          uniqueKey: '',
                          requiresConsent: false,
                          env: ['production']
                        });
                      }}
                      className="px-4 py-2 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-medium rounded-lg transition-colors"
                    >
                      + Add Your First Integration
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {integrations.map((integration) => (
                      <div
                        key={integration.id}
                        className="border border-[#E5E7EB] rounded-lg p-4 hover:border-[#1E40AF] transition-colors"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <h3 className="font-semibold text-[#111827] truncate max-w-full">{integration.id}</h3>
                              <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                                integration.enabled
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {integration.enabled ? 'Enabled' : 'Disabled'}
                              </span>
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium whitespace-nowrap">
                                {integration.type}
                              </span>
                              <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium whitespace-nowrap">
                                {integration.placement}
                              </span>
                            </div>
                            <p className="text-sm text-[#6B7280] mb-2 font-mono break-words overflow-hidden" style={{ wordBreak: 'break-all', maxHeight: '3em', lineHeight: '1.5em' }}>
                              {integration.content}
                            </p>
                            <div className="flex flex-wrap items-center gap-4 text-xs text-[#6B7280]">
                              {integration.uniqueKey && (
                                <span className="truncate max-w-xs">Key: {integration.uniqueKey}</span>
                              )}
                              {integration.requiresConsent && (
                                <span className="text-orange-600 whitespace-nowrap">Requires Consent</span>
                              )}
                              <span className="whitespace-nowrap">Env: {integration.env?.join(', ') || 'production'}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => handleToggleIntegration(integration)}
                              className={`px-3 py-1 rounded text-sm font-medium transition-colors whitespace-nowrap ${
                                integration.enabled
                                  ? 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                                  : 'bg-green-100 text-green-800 hover:bg-green-200'
                              }`}
                            >
                              {integration.enabled ? 'Disable' : 'Enable'}
                            </button>
                            <button
                              onClick={() => handleEditIntegration(integration)}
                              className="px-3 py-1 text-[#1E40AF] hover:text-[#1e3a8a] font-semibold text-sm whitespace-nowrap"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteIntegration(integration.id)}
                              className="px-3 py-1 text-red-600 hover:text-red-800 font-semibold text-sm whitespace-nowrap"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
